// Mission Table component - Table with sortable headers
// CREATED: Displays missions in a structured table format with column sorting
// Supports sorting by name, service, and duration

import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { MissionTableRow } from './MissionTableRow';
import type { TemplateWithItems } from '../../lib/templates';
import type { DropdownMenuItem } from '../ui/DropdownMenu';

export type SortField = 'name' | 'service' | 'estimated_duration_minutes';
export type SortOrder = 'asc' | 'desc';

interface MissionTableProps {
  templates: TemplateWithItems[];
  expandedMissionId: string | null;
  onToggleExpand: (id: string) => void;
  getDropdownItems: (template: TemplateWithItems) => DropdownMenuItem[];
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
}

export function MissionTable({
  templates,
  expandedMissionId,
  onToggleExpand,
  getDropdownItems,
  sortField,
  sortOrder,
  onSort,
}: MissionTableProps) {
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown size={16} className="text-gray-400" />;
    }
    return sortOrder === 'asc'
      ? <ArrowUp size={16} className="text-primary-600" />
      : <ArrowDown size={16} className="text-primary-600" />;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {/* Mission column */}
              <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                >
                  <span>MISSION</span>
                  {getSortIcon('name')}
                </button>
              </th>

              {/* Service column */}
              <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <button
                  onClick={() => onSort('service')}
                  className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                >
                  <span>SERVICE</span>
                  {getSortIcon('service')}
                </button>
              </th>

              {/* Duration column */}
              <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <button
                  onClick={() => onSort('estimated_duration_minutes')}
                  className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                >
                  <span>DURÉE</span>
                  {getSortIcon('estimated_duration_minutes')}
                </button>
              </th>

              {/* Actions column */}
              <th className="text-right px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {templates.map((template) => (
              <MissionTableRow
                key={template.id}
                template={template}
                isExpanded={expandedMissionId === template.id}
                onToggleExpand={() => onToggleExpand(template.id)}
                dropdownItems={getDropdownItems(template)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
